import React, { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import Datetime from "react-datetime";
import "react-datetime/css/react-datetime.css";
import { getDataWithComp } from "../../../utils/SamuiUtils";

const ItemWhModal = ({
  formDetailList,
  showItemModal,
  handleItemClose,
  dataList,
  onRowSelectItem,
  selectedItemType,
  // ใหม่: เปิดเลือกทั้งหมดเป็นค่าเริ่มต้นเมื่อเปิดโมดัล
  defaultSelectAll = false,
}) => {
  const safeLower = (v) => (v == null ? "" : String(v)).toLowerCase();
  const getRowKey = (item) => item?.WHDoc_Id ?? item?.Item_Id;

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedItems, setSelectedItems] = useState([]);
  const [whFromDataList, setWhFromDataList] = useState([]);
  const [whToDataList, setWhToDataList] = useState([]);
  const [selectedWarehouseFrom, setSelectedWarehouseFrom] = useState("");
  const [selectedWarehouseFrom2, setSelectedWarehouseFrom2] = useState("");
  const [selectedWarehouseTo, setSelectedWarehouseTo] = useState("");
  const [selectedCreator, setSelectedCreator] = useState("");

  const [fromDate, setFromDate] = useState(moment().startOf("month"));
  const [toDate, setToDate] = useState(moment().endOf("month"));

  const selectAllRef = useRef(null);
  // ใหม่: ป้องกันการ auto select ซ้ำหลายครั้งต่อการเปิดโมดัล
  const didAutoSelectAll = useRef(false);

  const handleChangeDate = (value, key) => {
    const m = moment.isMoment(value) ? value : moment(value);
    if (!m.isValid()) {
      if (key === "fromDate") setFromDate(null);
      if (key === "toDate") setToDate(null);
      return;
    }
    if (key === "fromDate") setFromDate(m);
    if (key === "toDate") setToDate(m);
  };

  const dedupByKey = (arr = []) => {
    const m = new Map();
    for (const it of arr || []) {
      const k = getRowKey(it);
      if (k != null && !m.has(k)) m.set(k, it);
    }
    return Array.from(m.values());
  };

  const isSelected = (item) =>
    (selectedItems || []).some((s) => getRowKey(s) === getRowKey(item));

  const whDocRemarkMap = useMemo(() => {
    const m = {};
    for (const it of selectedItems || []) {
      m[getRowKey(it)] = it?.WHDoc_Remark ?? "";
    }
    return m;
  }, [selectedItems]);

  const whDocRemarks = useMemo(() => {
    return Array.from(
      new Set(
        (selectedItems || [])
          .map((it) => String(it?.WHDoc_Remark ?? "").trim())
          .filter(Boolean)
      )
    );
  }, [selectedItems]);

  // unique creators list (sorted)
  const creatorOptions = useMemo(() => {
    const set = new Set(
      (dataList || [])
        .map((d) =>
          d?.WHDoc_CreatedBy == null ? "" : String(d.WHDoc_CreatedBy).trim()
        )
        .filter((s) => s)
    );
    return Array.from(set).sort((a, b) => a.localeCompare(b, "th"));
  }, [dataList]);

  const handleChangeWarehouseFrom = (whId) => {
    setSelectedWarehouseFrom(whId || "");
    const name =
      whFromDataList.find((wh) => String(wh.WH_Id) === String(whId))?.WH_Name ||
      "";
    setSelectedWarehouseFrom2(name);
  };
  const handleChangeWarehouseTo = (whId) => setSelectedWarehouseTo(whId || "");

  const filteredItemDataList = useMemo(() => {
    const term = safeLower(searchTerm).trim();
    const tokens = term.split(/\s+/).filter(Boolean);

    const buildSearchText = (item) => {
      const createdDateFormatted = item?.WHDoc_CreatedDate
        ? [
            moment(item.WHDoc_CreatedDate)
              .add(543, "years")
              .format("DD-MM-YYYY HH:mm"),
            moment(item.WHDoc_CreatedDate)
              .add(543, "years")
              .format("DD/MM/YYYY HH:mm"),
            moment(item.WHDoc_CreatedDate).format("YYYY-MM-DD HH:mm"),
          ].join(" ")
        : "";

      return safeLower(
        [
          item?.WHDoc_No,
          item?.WHDocType_Name,
          item?.F_WH_Name,
          item?.T_WH_Name,
          item?.WHDoc_Remark,
          item?.WHDoc_CreatedBy,
          createdDateFormatted,
          item?.Item_Code,
          item?.Item_Name,
        ]
          .filter(Boolean)
          .join(" ")
      );
    };

    const inDateRange = (createdAt) => {
      if (!createdAt) return true;
      const d = moment(createdAt);
      if (!d.isValid()) return true;
      const startOK = fromDate
        ? d.isSameOrAfter(moment(fromDate).startOf("day"))
        : true;
      const endOK = toDate ? d.isSameOrBefore(moment(toDate).endOf("day")) : true;
      return startOK && endOK;
    };

    const matchesWarehouseFrom = (item) => {
      if (!selectedWarehouseFrom) return true;
      return String(item?.F_WH_Id) === String(selectedWarehouseFrom);
    };
    const matchesWarehouseTo = (item) => {
      if (!selectedWarehouseTo) return true;
      // Only enforce To warehouse filter if WHDoc_Type === 2
      if (String(item?.WHDoc_Type) !== "2") return true;
      return String(item?.T_WH_Id) === String(selectedWarehouseTo);
    };

    const matchesCreator = (item) => {
      if (!selectedCreator) return true;
      return String(item?.WHDoc_CreatedBy || "").trim() === selectedCreator;
    };

    return (dataList || []).filter((item) => {
      const haystack = buildSearchText(item);
      const matchesSearch =
        tokens.length === 0 ? true : tokens.every((t) => haystack.includes(t));
      const matchesDate = inDateRange(item?.WHDoc_CreatedDate);
      const matchesWhFrom = matchesWarehouseFrom(item);
      const matchesWhTo = matchesWarehouseTo(item);
      const matchesByCreator = matchesCreator(item);
      const typeMatch =
        selectedItemType == null
          ? true
          : String(item?.WHDoc_Type) === String(selectedItemType);
      if (!typeMatch) return false;

      return (
        matchesSearch &&
        matchesDate &&
        matchesWhFrom &&
        matchesWhTo &&
        matchesByCreator &&
        typeMatch
      );
    });
  }, [
    dataList,
    searchTerm,
    fromDate,
    toDate,
    selectedWarehouseFrom,
    selectedWarehouseTo,
    selectedCreator,
    selectedItemType,
  ]);

  const allFiltered = useMemo(
    () => dedupByKey(filteredItemDataList || []),
    [filteredItemDataList]
  );
  const allSelected =
    allFiltered.length > 0 && allFiltered.every((it) => isSelected(it));

  const someSelected =
    !allSelected &&
    (selectedItems || []).some((s) =>
      allFiltered.some((f) => getRowKey(f) === getRowKey(s))
    );

  const handleSelectAll = (checked) => {
    setSelectedItems(checked ? allFiltered : []);
  };

  const handleCheckboxChange = (row, checked) => {
    const key = getRowKey(row);
    setSelectedItems((prev) => {
      const exists = prev.some((s) => getRowKey(s) === key);
      if (checked && !exists) return [...prev, row];
      if (!checked && exists) return prev.filter((s) => getRowKey(s) !== key);
      return prev;
    });
  };

  useEffect(() => {
    if (!showItemModal) return;
    setSearchTerm("");

    const fromPromise = getDataWithComp(
      "Tb_Set_WH",
      "AND Flag_Vansale = 'N' AND WH_Status = 'Y' ORDER BY Flag_Vansale, WH_Name ASC"
    );
    const toPromise = getDataWithComp(
      "Tb_Set_WH",
      "AND WH_Status = 'Y' ORDER BY WH_Name ASC"
    );

    const loadWarehouses = async () => {
      try {
        const fromRows = await Promise.resolve(fromPromise);
        if (Array.isArray(fromRows)) setWhFromDataList(fromRows);
        const toRows = await Promise.resolve(toPromise);
        if (Array.isArray(toRows)) setWhToDataList(toRows);
      } catch (e) {
        // handle error
      }
    };
    loadWarehouses();
  }, [showItemModal]);

  useEffect(() => {
    if (!showItemModal) return;
    setSelectedItems(
      (formDetailList || [])
        .map((item) =>
          (dataList || []).find(
            (filtered) =>
              getRowKey(filtered) ===
              (item?.WHDoc_Id ?? item?.itemId ?? item?.Item_Id)
          )
        )
        .filter(Boolean)
    );
    // รีเซ็ตแฟล็กทุกครั้งที่เปิดโมดัลใหม่
    didAutoSelectAll.current = false;
  }, [showItemModal, formDetailList, dataList]);

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someSelected;
    }
  }, [someSelected]);

  // เลือกทั้งหมดอัตโนมัติเมื่อเปิดโมดัล (ครั้งเดียวต่อการเปิด)
  useEffect(() => {
    if (!showItemModal) return;
    if (!defaultSelectAll) return;
    if (didAutoSelectAll.current) return;
    // ถ้ายังไม่มีการเลือกมาก่อน ค่อย auto select
    if ((selectedItems || []).length === 0 && (allFiltered || []).length > 0) {
      setSelectedItems(allFiltered);
      didAutoSelectAll.current = true;
    }
  }, [showItemModal, defaultSelectAll, allFiltered, selectedItems]);

  return (
    <>
      <div
        className={`modal ${showItemModal ? "show" : ""}`}
        style={{ display: showItemModal ? "block" : "none" }}
      >
        <div className="modal-dialog modal-lg" role="document">
          <div
            className="modal-content"
            style={{ minWidth: "1225px", left: "-30%" }}
          >
            <div className="modal-header">
              <h5 className="modal-title">รายการเอกสาร</h5>
              <button type="button" className="close" onClick={handleItemClose}>
                <span>×</span>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <input
                  type="text"
                  className="form-control"
                  placeholder="ค้นหา เลขเอกสาร/หมายเหตุ"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "auto auto",
                    gap: "8px",
                    justifyContent: "left",
                    alignItems: "baseline",
                  }}
                >
                  {" "}
                  <div className="d-flex mt-2 flex-wrap gap-3 align-items-center">
                    <div className="d-flex align-items-center gap-2">
                      <label
                        className="mb-0 me-2"
                        style={
                          selectedItemType !== 2 ? { width: "75px" } : { width: "45px" }
                        }
                      >
                        {selectedItemType !== 2 ? "คลังที่ปรับปรุง\t:" : "จากคลัง :"}
                      </label>
                      <select
                        className="form-select"
                        value={selectedWarehouseFrom}
                        onChange={(e) => handleChangeWarehouseFrom(e.target.value)}
                        style={{ width: "140px" }}
                      >
                        <option value="">กรุณาระบุคลัง</option>
                        {whFromDataList.map((warehouse) => (
                          <option key={warehouse.WH_Id} value={warehouse.WH_Id}>
                            {warehouse.WH_Name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* เงื่อนไข: แสดง ไปยังคลัง ถ้า WHDoc_Type === 2 ในผลลัพธ์ */}
                    {filteredItemDataList.some((i) => String(i?.WHDoc_Type) === "2") && (
                      <div className="d-flex align-items-center gap-2">
                        <label className="mb-0 me-2" style={{ width: "45px" }}>
                          ไปยังคลัง :
                        </label>
                        <select
                          className="form-select"
                          value={selectedWarehouseTo}
                          onChange={(e) => handleChangeWarehouseTo(e.target.value)}
                          style={{ width: "140px" }}
                        >
                          <option value="">กรุณาระบุคลัง</option>
                          {whToDataList.map((warehouse) => (
                            <option key={warehouse.WH_Id} value={warehouse.WH_Id}>
                              {warehouse.WH_Name}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {/* ผู้สร้าง */}
                    <div className="d-flex align-items-center gap-2">
                      <label className="mb-0 me-2" style={{ width: "45px" }}>
                        ผู้สร้าง :
                      </label>
                      <select
                        className="form-select"
                        value={selectedCreator}
                        onChange={(e) => setSelectedCreator(e.target.value)}
                        style={{ width: "140px" }}
                      >
                        <option value="">ทุกคน</option>
                        {creatorOptions.map((name) => (
                          <option key={name} value={name}>
                            {name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "200px 200px",
                      gap: "8px",
                    }}
                  >
                    <div>
                      <div
                        className="input-group"
                        style={{
                          width: "100%",
                          display: "grid",
                          gridTemplateColumns: "90px 110px",
                        }}
                      >
                        <div>
                          <span className="input-group-text">จากวันที่สร้าง</span>
                        </div>
                        <div>
                          <Datetime
                            name="fromDate"
                            value={fromDate ? moment(fromDate).add(543, "years") : null}
                            onChange={(date) =>
                              handleChangeDate(moment(date).subtract(543, "years"), "fromDate")
                            }
                            dateFormat="DD-MM-YYYY"
                            timeFormat={false}
                            inputProps={{ readOnly: true, disabled: false }}
                          />
                        </div>
                      </div>
                    </div>
                    <div>
                      <div
                        className="input-group"
                        style={{
                          width: "100%",
                          display: "grid",
                          gridTemplateColumns: "80px 110px",
                        }}
                      >
                        <div>
                          <span className="input-group-text">ถึงวันที่สร้าง</span>
                        </div>
                        <div>
                          <Datetime
                            name="toDate"
                            value={toDate ? moment(toDate).add(543, "years") : null}
                            onChange={(date) =>
                              handleChangeDate(moment(date).subtract(543, "years"), "toDate")
                            }
                            dateFormat="DD-MM-YYYY"
                            timeFormat={false}
                            inputProps={{ readOnly: true, disabled: false }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="table-responsive" style={{ maxHeight: "600px", overflowY: "auto" }}>
                <table className="table table-striped table-hover">
                  <thead className="thead-dark">
                    <tr>
                      <th className="text-center">
                        <input
                          type="checkbox"
                          ref={selectAllRef}
                          checked={allSelected}
                          onChange={(e) => handleSelectAll(e.target.checked)}
                        />
                      </th>
                      <th className="text-center">เลขเอกสาร</th>
                      <th className="text-center">ประเภท</th>
                      <th className="text-center">คลังที่ปรับปรุง</th>
                      {selectedItemType === 2 && (
                        <th className="text-center">ปลายทาง</th>
                      )}
                      <th className="text-center">วันที่สร้าง</th>
                      <th className="text-center">หมายเหตุ</th>
                      <th className="text-center">ชื่อผู้สร้าง</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredItemDataList.length > 0 ? (
                      filteredItemDataList.map((data, index) => (
                        <tr key={getRowKey(data) ?? index}>
                          <td className="text-center">
                            <input
                              type="checkbox"
                              checked={isSelected(data)}
                              onChange={(e) => handleCheckboxChange(data, e.target.checked)}
                            />
                          </td>
                          <td className="text-center">{data?.WHDoc_No ?? "-"}</td>
                          <td className="text-center">{data?.WHDocType_Name ?? "-"}</td>
                          <td className="text-center">{data?.F_WH_Name ?? "-"}</td>
                          {selectedItemType === 2 && (
                            <td className="text-center">{data?.T_WH_Name ?? "-"}</td>
                          )}
                          <td className="text-center">
                            {data?.WHDoc_CreatedDate
                              ? moment(data.WHDoc_CreatedDate).add(543, "years").format("DD-MM-YYYY HH:mm")
                              : "-"}
                          </td>
                          <td className="text-left">{data?.WHDoc_Remark ?? "-"}</td>
                          <td className="text-center">{data?.WHDoc_CreatedBy ?? "-"}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={8}>
                          <div className="text-center py-4">
                            <h5>ไม่พบข้อมูล</h5>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-primary"
                onClick={() =>
                  onRowSelectItem(dedupByKey(selectedItems), {
                    fromDate,
                    toDate,
                    selectedWarehouseFrom,
                    selectedWarehouseTo,
                    selectedCreator,
                    whDocRemarks,
                    whDocRemarkMap,
                    selectedWarehouseFrom2,
                  })
                }
                disabled={(selectedItems || []).length === 0}
              >
                ยืนยัน
              </button>
              <button className="btn btn-danger" onClick={handleItemClose}>
                ปิด
              </button>
            </div>
          </div>
        </div>
      </div>
      {showItemModal && <div className="modal-backdrop fade show"></div>}
    </>
  );
};

export default ItemWhModal;
